import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createMapStyleLifecycle } from "./mapStyleLifecycle.js";

/** React adapter; consumers retain map ownership and provide one reconcile. */
export function useMapStyleLifecycle({
  mapRef,
  style,
  reconcile,
  onError
}) {
  const reconcileRef = useRef(reconcile);
  const onErrorRef = useRef(onError);
  const lifecycleRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(() => mapRef.current || null);
  const [readyRevision, setReadyRevision] = useState(0);
  reconcileRef.current = reconcile;
  onErrorRef.current = onError;
  useEffect(() => {
    if (mapRef.current) {
      setMapInstance(mapRef.current);
      return undefined;
    }
    let frameId;
    const waitForMap = () => {
      if (mapRef.current) {
        setMapInstance(mapRef.current);
        return;
      }
      frameId = requestAnimationFrame(waitForMap);
    };
    frameId = requestAnimationFrame(waitForMap);
    return () => cancelAnimationFrame(frameId);
  }, [mapRef]);
  useEffect(() => {
    if (!mapInstance) return undefined;
    const lifecycle = createMapStyleLifecycle({
      map: mapInstance,
      onError: (error, revision) => onErrorRef.current?.(error, revision),
      onReady: async (revision_0, isCurrent) => {
        await reconcileRef.current?.({
          revision: revision_0,
          isCurrent
        });
        if (isCurrent(revision_0)) setReadyRevision(revision_0);
      }
    });
    lifecycleRef.current = lifecycle;
    return () => {
      lifecycle.dispose();
      if (lifecycleRef.current === lifecycle) lifecycleRef.current = null;
    };
  }, [mapInstance]);
  useEffect(() => {
    if (style === undefined || !lifecycleRef.current) return;
    setReadyRevision(0);
    lifecycleRef.current.beginStyle(style);
  }, [mapInstance, style]);
  const withCurrentStyle = useCallback((revision_1, callback) => {
    const lifecycle_0 = lifecycleRef.current;
    if (!lifecycle_0?.isCurrent(revision_1)) return undefined;
    return callback();
  }, []);
  const requestReconcile = useCallback(() => lifecycleRef.current?.reconcile(), []);
  return useMemo(() => ({
    readyRevision,
    withCurrentStyle,
    requestReconcile
  }), [readyRevision, requestReconcile, withCurrentStyle]);
}